import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/store";
import { VacancyStatus } from "../../enums/VacancyStatus";
import { vacancyService } from "../../infrastructure/vacancy-service";
import { CreateVacancyCommand } from "../../dtos/CreateVacancyDto";
import { VacancyListItemDto } from "../../dtos/VacancyListItemDto";
import { VacancyDto } from "../../dtos/VacancyDto";

interface VacancyState {
  items: VacancyListItemDto[];
  currentVacancy: VacancyDto | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: VacancyState = {
  items: [],
  currentVacancy: null,
  isLoading: false,
  error: null,
};

export const fetchVacancies = createAsyncThunk<
  VacancyListItemDto[],
  | {
      organizationId?: string;
      searchTerm?: string;
      status?: VacancyStatus | "All";
    }
  | undefined,
  { rejectValue: string }
>("vacancies/fetchAll", async (args, { rejectWithValue }) => {
  try {
    const { organizationId, searchTerm, status } = args || {};
    let vacancies: VacancyListItemDto[] = [];

    if (status && status !== "All") {
      vacancies = await vacancyService.getVacanciesByStatus(status);
      // Hybrid approach: Filter by search term client-side if both are present
      if (searchTerm) {
        const lowerTerm = searchTerm.toLowerCase();
        vacancies = vacancies.filter(
          (v) =>
            v.title.toLowerCase().includes(lowerTerm) ||
            v.description?.toLowerCase().includes(lowerTerm),
        );
      }
    } else {
      if (organizationId) {
        vacancies = await vacancyService.getVacancies(
          organizationId,
          searchTerm,
        );
      }
    }
    return vacancies;
  } catch (err) {
    const message =
      (err as Record<string, Record<string, Record<string, string>>>)?.response
        ?.data?.message ?? "Failed to load vacancies";
    return rejectWithValue(message);
  }
});

export const fetchVacancyById = createAsyncThunk<
  VacancyDto,
  { organizationId: string; id: string },
  { rejectValue: string }
>(
  "vacancies/fetchById",
  async ({ organizationId, id }, { rejectWithValue }) => {
    try {
      return await vacancyService.getVacancyById(organizationId, id);
    } catch (err) {
      const message =
        (err as Record<string, Record<string, Record<string, string>>>)
          ?.response?.data?.message ?? "Failed to load vacancy details";
      return rejectWithValue(message);
    }
  },
);

export const createVacancy = createAsyncThunk<
  void,
  CreateVacancyCommand,
  { rejectValue: string }
>("vacancies/create", async (command, { rejectWithValue, dispatch }) => {
  try {
    await vacancyService.createVacancy(command);
    dispatch(fetchVacancies());
  } catch (err) {
    const message =
      (err as Record<string, Record<string, Record<string, string>>>)?.response
        ?.data?.message ?? "Failed to create vacancy";
    return rejectWithValue(message);
  }
});

export const updateVacancy = createAsyncThunk<
  void,
  CreateVacancyCommand,
  { rejectValue: string }
>("vacancies/update", async (command, { rejectWithValue, dispatch }) => {
  try {
    const { vacancyId, ...updateData } = command;
    await vacancyService.updateVacancy(vacancyId || "", updateData);
    dispatch(fetchVacancies());
  } catch (err) {
    const message =
      (err as Record<string, Record<string, Record<string, string>>>)?.response
        ?.data?.message ?? "Failed to update vacancy";
    return rejectWithValue(message);
  }
});

export const updateVacancyStatus = createAsyncThunk<
  boolean,
  { organizationId: string; vacancyId: string; status: VacancyStatus },
  { rejectValue: string }
>(
  "vacancies/updateStatus",
  async (
    { organizationId, vacancyId, status },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const result = await vacancyService.updateVacancyStatus(
        organizationId,
        vacancyId,
        status,
      );

      dispatch(fetchVacancies());
      return result;
    } catch (err) {
      const message =
        (err as Record<string, Record<string, Record<string, string>>>)
          ?.response?.data?.message ?? "Failed to update vacancy status";
      return rejectWithValue(message);
    }
  },
);

export const notifyClosedVacancyCandidates = createAsyncThunk<
  number,
  { organizationId: string; vacancyId: string },
  { rejectValue: string }
>(
  "vacancies/notifyClosedCandidates",
  async ({ organizationId, vacancyId }, { rejectWithValue }) => {
    try {
      const response = await vacancyService.notifyClosedVacancyCandidates(
        organizationId,
        vacancyId,
      );

      if (!response.isSuccess) {
        return rejectWithValue(
          response.error ?? "Failed to send regret messages",
        );
      }

      return response.value ?? 0;
    } catch (err) {
      const apiMessage =
        (err as Record<string, Record<string, string>>)?.response?.data ??
        (err as Record<string, Record<string, Record<string, string>>>)
          ?.response?.data?.message ??
        "Failed to send regret messages";

      return rejectWithValue(apiMessage);
    }
  },
);

export const deleteVacancy = createAsyncThunk<
  void,
  { organizationId: string; id: string },
  { rejectValue: string }
>(
  "vacancies/delete",
  async ({ organizationId, id }, { rejectWithValue, dispatch }) => {
    try {
      await vacancyService.deleteVacancy(organizationId, id);
      dispatch(fetchVacancies());
    } catch (err) {
      const message =
        (err as Record<string, Record<string, Record<string, string>>>)
          ?.response?.data?.message ?? "Failed to delete vacancy";
      return rejectWithValue(message);
    }
  },
);

export const postVacancyToCareers24 = createAsyncThunk<
  { success: boolean; message?: string; vacancyUrl?: string },
  { organizationId: string; vacancyId: string },
  { rejectValue: string }
>(
  "vacancies/postToCareers24",
  async ({ organizationId, vacancyId }, { rejectWithValue }) => {
    try {
      return await vacancyService.postVacancyToCareers24(
        organizationId,
        vacancyId,
      );
    } catch (error: unknown) {
      return rejectWithValue(
        (error as Error)?.message ?? "Failed to post vacancy to Careers24",
      );
    }
  },
);

const vacancySlice = createSlice({
  name: "vacancies",
  initialState,
  reducers: {
    clearCurrentVacancy: (state) => {
      state.currentVacancy = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVacancies.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVacancies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
      })
      .addCase(fetchVacancies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? "Failed to load vacancies";
      })
      .addCase(fetchVacancyById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.currentVacancy = null;
      })
      .addCase(fetchVacancyById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentVacancy = action.payload;
      })
      .addCase(fetchVacancyById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? "Failed to load vacancy";
      });
  },
});

export const { clearCurrentVacancy } = vacancySlice.actions;
export default vacancySlice.reducer;

export const selectVacancyState = (state: RootState) => state.vacancies;
export const selectVacancies = (state: RootState) => state.vacancies.items;
export const selectCurrentVacancy = (state: RootState) =>
  state.vacancies.currentVacancy;
export const selectVacanciesLoading = (state: RootState) =>
  state.vacancies.isLoading;
export const selectVacanciesError = (state: RootState) => state.vacancies.error;
